<script>
import {computed, onMounted, ref} from "vue";
import useValidation from "@/use/Validation";
import useSharedData from "@/use/SharedData";
import useSharedMethod from "@/use/SharedMethod";
import useTools from "@/use/Tools";
import {useCategoryStore} from "@/stores/Grille/Category";
import axios from "axios";

export default {
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        viewAction: Boolean,
        deleteAction: Boolean,
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction);
        const {mxRules} = useValidation();
        const {dialog, loading, overlay} = useSharedData();
        const {getBack} = useSharedMethod();
        const {$refs, $route} = useTools();
        const categoryStore = useCategoryStore();

        const category = ref({
            byValue: false,
            byRung: false,
        });

        function findById(id) {
            loading.value = true;
            axios.get(`/categories/${id}`).then((response) => {
                category.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }


        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/categories', category.value).then((response) => {
                    categoryStore.addCategory(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/categories', category.value).then((response) => {
                    categoryStore.updateCategory(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.delete(`/categories/${category.value.id}`).then(() => {
                    categoryStore.deleteCategory(category.value);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            categoryStore,
            mxRules,
            readonly,
            dialog,
            loading,
            overlay,
            category,
            save,
            update,
            remove,
            getBack,
        };
    }
};
</script>

<template>
    <v-dialog
        transition="dialog-bottom-transition"
        v-model="dialog"
        persistent
        max-width="700">
        <v-card flat>
            <w-dialog-toolbar
                :view-action="viewAction"
                :delete-action="deleteAction"
                :add-action="addAction"
                :update-action="updateAction"
            ></w-dialog-toolbar>
            <w-overlay
                :overlay="overlay"
                :loading="loading"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            ></w-overlay>
            <v-card-text>
                <v-container fluid>
                    <v-form ref="form" lazy-validation>
                        <w-fieldset :title="$t('common.detail')">
                            <v-row dense>
                                <v-col cols="12">
                                    <w-label :label="$t('common.name')" required/>
                                    <w-text-field
                                        v-model="category.name"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('module.classe')" re required/>
                                    <str-worker-def-autocomplete
                                        v-model="category.classeId"
                                        resource="classes"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('category.base_salary')" required/>
                                    <w-text-field
                                        v-model="category.baseSalary"
                                        type="number"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <br/>
                        <w-fieldset :title="$t('common.increase')">
                            <v-row dense>
                                <v-col cols="6">
                                    <w-label :label="$t('pay_slip.rung')" required/>
                                    <w-enum-select
                                        v-model="category.byRung"
                                        :items="[
                                            {id: false, name: $t('common.without_rung')},
                                            {id: true, name: $t('common.with_rung')}
                                         ]"
                                        :readonly="readonly"
                                        not-clearable
                                    />
                                </v-col>
                                <v-col cols="6" v-if="category.byRung">
                                    <w-label :label="$t('common.periodicity')" required/>
                                    <w-text-field
                                        v-model="category.periodicity"
                                        type="number"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6" v-if="category.byRung">
                                    <w-label :label="$t('common.increase')" required/>
                                    <w-enum-select
                                        v-model="category.byValue"
                                        :items="[
                                            {id: false, name: $t('common.by_percent')},
                                            {id: true, name: $t('common.by_value')}
                                        ]"
                                        :readonly="readonly"
                                        not-clearable
                                    />
                                </v-col>
                                <v-col cols="6" v-if="category.byValue && category.byRung">
                                    <w-label :label="$t('category.value')" required/>
                                    <w-text-field
                                        v-model="category.value"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6" v-if="!category.byValue && category.byRung">
                                    <w-label :label="$t('common.percent')" required/>
                                    <w-text-field
                                        v-model="category.percenatge"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-form>
                </v-container>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add :show="addAction" :loading="loading" @press="save"/>
                <w-btn-update :show="updateAction" :loading="loading" @press="update"/>
                <w-btn-delete :show="deleteAction" @press="overlay = true"/>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
